import { useTranslation } from "react-i18next";

import type { PrizeDropPrize } from "@/campaigns/modules/prizedrop/types";
import { PrizeGamesChips } from "@/components/campaigns/prize-games-chips";
import { PrizeText } from "@/components/prize-text";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatPrizeTitle } from "@/utils/helpers";

export interface PrizeDropRowProps {
  prize: PrizeDropPrize;
}

/**
 * One prize tier of the drop: the prize, how many of it are left, and the games it applies to.
 *
 * The same card every other row on a campaign page is (the mission rungs, the How-to cards):
 * the outer radius, the default surface, a title row with the figure at the far end, and the
 * shared progress bar beneath — in the prize gold, because this bar is not the player's
 * progress but the prize stock itself, shrinking as units drop. A tier with nothing left reads
 * as finished the way a completed rung does: dimmed, no bar, a quiet status in place of the
 * count.
 */
export const PrizeDropRow = ({ prize }: PrizeDropRowProps) => {
  const { t } = useTranslation();
  const depleted = prize.left_to_win <= 0;
  const pct =
    prize.quantity > 0 ? Math.min(1, prize.left_to_win / prize.quantity) : 0;

  return (
    <Card className={cn("flex flex-col gap-2.5", depleted && "opacity-55")}>
      <div className="flex w-full items-center justify-between gap-3">
        {/* Never truncated: the prize is the row's priority content (CLAUDE.md) — it shrinks,
            then glides on its one line. */}
        <PrizeText
          size="small"
          weight="bold"
          icon
          fit="marquee"
          className={cn("min-w-0 flex-1", depleted && "text-muted-foreground")}
        >
          {formatPrizeTitle(prize.label, t)}
        </PrizeText>
        {depleted ? (
          <Badge variant="text" color="locked" className="shrink-0">
            {t("prizedrop.allDropped", "All dropped")}
          </Badge>
        ) : (
          <>
            <Badge
              variant="text"
              color="prizes"
              className="shrink-0"
              aria-hidden
            >
              {prize.left_to_win.toLocaleString()}
              <span className="font-semibold text-muted-foreground">
                {" / "}
                {prize.quantity.toLocaleString()}
              </span>
            </Badge>
            {/* The count as one phrase for assistive tech, read right after the prize (the
                visible figure is split across two tones and hidden from it). Not a label on the
                card: that made every tier a landmark named after a number. */}
            <span className="sr-only">
              {t("prizedrop.leftOfTotal", "{{left}} of {{total}} left", {
                left: prize.left_to_win.toLocaleString(),
                total: prize.quantity.toLocaleString(),
              })}
            </span>
          </>
        )}
      </div>
      <PrizeGamesChips
        games={prize.games ?? []}
        count={prize.games_count}
        className="flex w-full flex-wrap items-center gap-1.5 overflow-hidden"
      />
      {!depleted && (
        <div className="task-progress-track progress-prizes" aria-hidden>
          <div
            className="task-progress-fill"
            // 2% floor keeps a last unit visible; zero never renders (the tier is depleted).
            style={{ width: `${Math.max(2, Math.round(pct * 100))}%` }}
          />
        </div>
      )}
    </Card>
  );
};
